// Экраны 6 «История платежей» и 7 «Отмена подписки» (только автопродление, см. её исходный
// докстринг ниже) из ТЗ раздел 3.3 — объединены на одной странице по запросу пользователя
// 2026-08-20: изначально это были отдельные вкладки, но при 7 вкладках нижний таббар не
// помещался на узкий экран. Каждый раздел ниже — самостоятельный блок состояния/логики (свой
// fetch/своя ошибка), просто отрисован на одной странице подряд, а не по отдельным маршрутам —
// сама бизнес-логика не менялась.
//
// Экран 8 «Промокоды» здесь БОЛЬШЕ НЕТ (был третьим разделом на этой странице) — по запросу
// пользователя 2026-08-20 перенесён на CheckoutScreen.tsx (под кнопку "Оплатить"): логически он
// ближе к покупке, чем к отмене/истории платежей. Компонент — src/components/PromoCodeSection.tsx,
// сама логика не менялась, только место.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, cancelAutoRenew, fetchPayments, resumeAutoRenew, type Payment } from "../api/client";
import { useSubscription } from "../hooks/useSubscription";

const PAYMENT_STATUS_KEYS: Record<Payment["status"], string> = {
  pending: "payment_status_pending",
  succeeded: "payment_status_succeeded",
  failed: "payment_status_failed",
  refunded: "payment_status_refunded",
};

const PAYMENT_TYPE_KEYS: Record<Payment["type"], string> = {
  purchase: "payment_type_purchase",
  renewal: "payment_type_renewal",
  upgrade: "payment_type_upgrade",
  refund: "payment_type_refund",
};

function PaymentsSection() {
  const { t, i18n } = useTranslation();
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dateLocale = i18n.language === "en" ? "en-US" : "ru-RU";

  useEffect(() => {
    let cancelled = false;
    fetchPayments()
      .then((data) => {
        if (!cancelled) setPayments(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <h2>{t("payments_title")}</h2>
      {error && <p role="alert">{t("payments_error")}</p>}
      {!error && payments === null && <p>{t("loading")}</p>}
      {payments !== null && payments.length === 0 && <p>{t("payments_empty")}</p>}
      {payments !== null && payments.length > 0 && (
        <ul>
          {payments.map((payment) => (
            <li key={payment.id}>
              {new Date(payment.created_at).toLocaleDateString(dateLocale)} —{" "}
              {t(PAYMENT_TYPE_KEYS[payment.type])}: {payment.amount} {payment.currency} —{" "}
              {t(PAYMENT_STATUS_KEYS[payment.status])}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// НАМЕРЕННО урезанный скоп — только отмена/возобновление автопродления (см.
// app/api/routes/miniapp/subscriptions.py::cancel_auto_renew/resume_auto_renew). Немедленная
// отмена с частичным возвратом средств не реализована — упирается в нерешённую политику возврата
// и отсутствие admin-панели для ручного процесса (см. docs/OPEN_QUESTIONS.md). Подписка при
// отключённом автопродлении остаётся полноценно активной до конца оплаченного периода — это не
// "отменённая" подписка в смысле статуса, только отметка "не продлевать".
function CancelSection() {
  const { t } = useTranslation();
  const { subscription, loading, error, setSubscription, setError } = useSubscription();
  const [submitting, setSubmitting] = useState(false);

  const handleToggle = () => {
    if (!subscription) return;
    setSubmitting(true);
    setError(null);
    const action = subscription.auto_renew ? cancelAutoRenew : resumeAutoRenew;
    action()
      .then(setSubscription)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setSubmitting(false));
  };

  return (
    <section>
      <h2>{t("cancel_title")}</h2>
      {error && <p role="alert">{error}</p>}
      {loading && <p>{t("loading")}</p>}
      {!loading && subscription === null && <p>{t("subscription_none")}</p>}
      {subscription && (
        <div>
          <p>
            {t("cancel_auto_renew_status")}:{" "}
            <strong>
              {subscription.auto_renew ? t("cancel_auto_renew_on") : t("cancel_auto_renew_off")}
            </strong>
          </p>
          {!subscription.auto_renew && <p>{t("cancel_note_still_active")}</p>}
          <button type="button" onClick={handleToggle} disabled={submitting}>
            {submitting
              ? t("loading")
              : subscription.auto_renew
                ? t("cancel_action_cancel")
                : t("cancel_action_resume")}
          </button>
        </div>
      )}
    </section>
  );
}

export function MoreScreen() {
  const { t } = useTranslation();
  return (
    <div>
      <h1>{t("more_title")}</h1>
      <PaymentsSection />
      <CancelSection />
    </div>
  );
}
